import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { faCheck } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import * as Backend from '@spacefill/shared/src/utils/Backend';
import { formatRelativeDate } from '@spacefill/shared/src/utils/Date';
import { getWarehouseLabel } from '@spacefill/shared/src/utils/Warehouse';
import { useUserContext } from '@spacefill/shared/src/utils/contexts/UserContext';
import { Avatar } from '@spacefill/uikit/src/components/Avatar/Avatar';
import { Divider } from '@spacefill/uikit/src/components/Divider';
import { EmptyState } from '@spacefill/uikit/src/components/EmptyState';
import { Loader } from '@spacefill/uikit/src/components/Loader';
import { SkeletonRow } from '@spacefill/uikit/src/components/Skeletons/SkeletonRow';
import { toast } from '@spacefill/uikit/src/components/Toast/createToast';

import emptyNotificationSVG from '../../assets/empty-states/empty-state-notification.svg';
import { templates } from './InAppNotificationsTemplatesLegacy';

const PER_PAGE = 10;

function NotificationSkeleton() {
    return (
        <div className='py-3'>
            <SkeletonRow
                rows={[['w-1/3', 'w-1/3'], ['w-1/3', 'w-2/5', 'w-20'], 'w-full', 'w-1/2']}
                height='h-4'
            />
        </div>
    );
}

export function InAppNotificationsListLegacy({ unreadCount }) {
    const { user } = useUserContext();
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [showInitialLoading, setShowInitialLoading] = useState(true);

    const { data, isFetchingNextPage, hasNextPage, fetchNextPage, isFetching, isLoading } = useInfiniteQuery({
        queryKey: ['in-app-notifications', PER_PAGE],
        queryFn: ({ pageParam = 0 }) =>
            Backend.call('in-app-notifications', { limit: PER_PAGE, offset: pageParam * PER_PAGE }),
        getNextPageParam: (lastPage) => {
            return lastPage.nextPage ?? undefined; // undefined is important to have `hasNextPage` false
        },
        refetchOnWindowFocus: true,
    });

    useEffect(() => {
        if (!isLoading && !isFetching && data?.pages?.[0]) {
            setShowInitialLoading(false);
        }
    }, [isLoading, isFetching, data]);

    const { mutate: readMutate } = useMutation({
        mutationFn: ({ notificationId }) => Backend.call(`in-app-notifications/${notificationId}/read`, {}, 'POST'),
        onSuccess() {
            queryClient.invalidateQueries({
                queryKey: ['in-app-notifications'],
            });
            queryClient.invalidateQueries({
                queryKey: ['/in-app-notifications/unread'],
            });
        },
        onError(error) {
            console.error('error during read mutation: ', error);
            toast.error(t('Internal error!'));
        },
    });

    const observer = useRef();
    const lastProjectElementRef = useCallback(
        (node) => {
            if (isFetchingNextPage) return;
            if (observer.current) observer.current.disconnect();
            observer.current = new window.IntersectionObserver((entries) => {
                if (entries[0].isIntersecting && hasNextPage) {
                    fetchNextPage();
                }
            });
            if (node) observer.current.observe(node);
        },
        [isFetchingNextPage, fetchNextPage, hasNextPage]
    );

    const totalCount = data?.pages?.[0]?.count;
    const loadedCount = data?.pages?.reduce((acc, page) => acc + (page.data?.length || 0), 0) || 0;
    const remainingCount = totalCount - loadedCount;
    const skeletonCount = showInitialLoading
        ? Math.min(unreadCount || 4, 4)
        : isFetching
          ? Math.min(remainingCount || 4, 4)
          : 0;

    return (
        <div>
            {!showInitialLoading && totalCount === 0 && (
                <EmptyState
                    picture={{
                        src: emptyNotificationSVG,
                        width: 80,
                        height: 121,
                    }}
                    paragraph={t('No notification yet. New comments and alerts will be displayed here.')}
                />
            )}
            {(data?.pages || []).map((notifications, pageIndex) => {
                return (
                    <Fragment key={pageIndex}>
                        {(notifications.data || []).map((notification, notificationIndex) => {
                            if (!(notification.templateId in templates)) {
                                return null;
                            }
                            const notificationTemplate = templates[notification.templateId];
                            const isLastPage = pageIndex === data?.pages?.length - 1;
                            const isLastNotification = notificationIndex === notifications.data.length - 1;
                            const isLastElement = isLastPage && isLastNotification && !hasNextPage;

                            return (
                                <Link
                                    key={`InAppNotification-${notification.id}`}
                                    to={notificationTemplate.url(notification)}
                                    onClick={() => readMutate({ notificationId: notification.id })}
                                >
                                    <InAppNotification
                                        key={notification.id}
                                        title={notificationTemplate.title(notification)}
                                        titleIcon={notificationTemplate.icon(notification)}
                                        user={notification.templateParams.createdBy}
                                        createdFromRuleId={notification.templateParams.createdFromRuleId}
                                        receivedAt={new Date(notification.createdAt)}
                                        warehouseName={getWarehouseLabel(user, notification.templateParams.warehouse)}
                                        unread={!notification.readAt}
                                    >
                                        {notificationTemplate.content(notification)}
                                    </InAppNotification>
                                    {isLastElement ? <div className='h-4' /> : <Divider />}
                                </Link>
                            );
                        })}
                    </Fragment>
                );
            })}
            {(showInitialLoading || isFetching) && skeletonCount > 0 && (
                <>
                    {Array.from({ length: skeletonCount }).map((_, index) => (
                        <Fragment key={index}>
                            <NotificationSkeleton />
                            {index < skeletonCount - 1 && <Divider />}
                        </Fragment>
                    ))}
                </>
            )}
            {hasNextPage && (
                <div ref={lastProjectElementRef}>
                    <Loader />
                </div>
            )}
        </div>
    );
}

const InAppNotification = ({
    titleIcon,
    user,
    createdFromRuleId,
    receivedAt,
    unread = true,
    onMarkAsRead,
    dataTestid,
    htmlAttributes,
    children,
}) => (
    <div
        data-testid={dataTestid}
        {...htmlAttributes}
        data-component-name='InAppNotification'
        className='flex items-start gap-3 px-2 py-3 hover:bg-gray-50'
    >
        <div className='mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-500'>
            <FontAwesomeIcon icon={titleIcon} />
        </div>
        <div className='min-w-0 flex-1'>
            {user?.role === 'SERVICE' ? (
                <Avatar
                    dataTestid={dataTestid && `${dataTestid}-avatar`}
                    user={user}
                    showAsCompany
                    className='mb-1 text-sm font-medium'
                />
            ) : (
                <Avatar
                    dataTestid={dataTestid && `${dataTestid}-avatar`}
                    user={user}
                    createdFromRuleId={createdFromRuleId}
                    className='mb-1 text-sm font-medium'
                />
            )}
            <div
                data-testid={dataTestid && `${dataTestid}-content`}
                className='text-sm text-gray-600'
            >
                {children}
            </div>
        </div>
        <div className='flex shrink-0 flex-col items-end gap-1.5'>
            <span className='text-xs text-gray-400'>{formatRelativeDate(receivedAt)}</span>
            {unread ? (
                <button
                    type='button'
                    onClick={onMarkAsRead}
                    data-testid={dataTestid && `${dataTestid}-indicator`}
                    className='size-3 rounded-full bg-red-500 hover:bg-red-600'
                />
            ) : (
                <span
                    data-testid={dataTestid && `${dataTestid}-indicator`}
                    className={'flex size-3 items-center justify-center rounded-full text-gray-400'}
                >
                    <FontAwesomeIcon
                        icon={faCheck}
                        className='text-[8px]'
                    />
                </span>
            )}
        </div>
    </div>
);
